import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { ArrowRight, Loader2, Check } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const freightTypes = [
  { value: 'palletized', label: 'Palletized Freight' },
  { value: 'ltl', label: 'LTL (Less Than Truckload)' },
  { value: 'ftl', label: 'FTL (Full Truckload)' },
  { value: 'oversized', label: 'Oversized Load' },
  { value: 'equipment', label: 'Equipment / Heavy Machinery' },
  { value: 'other', label: 'Other' },
];

const accessorialOptions = [
  { id: 'commercial_dock', label: 'Commercial Dock' },
  { id: 'forklift_available', label: 'Forklift Available' },
  { id: 'liftgate_required', label: 'Liftgate Required' },
  { id: 'residential', label: 'Residential Pickup/Delivery' },
  { id: 'appointment_required', label: 'Appointment Required' },
];

export const QuoteSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [selectedAccessorials, setSelectedAccessorials] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    freight_type: '',
    pickup_zip: '',
    delivery_zip: '',
    description: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAccessorialChange = (accessorialId, checked) => {
    setSelectedAccessorials((prev) =>
      checked
        ? [...prev, accessorialId]
        : prev.filter((id) => id !== accessorialId)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      await axios.post(`${API}/quotes`, {
        ...formData,
        accessorials: selectedAccessorials,
        weight: '', // Optional from homepage
      });
      setIsSubmitted(true);
    } catch (error) {
      console.error('Quote submission error:', error);
      setSubmitError('Failed to submit quote. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section data-testid="quote-section" className="relative overflow-hidden">
      <div className="grid lg:grid-cols-2">
        {/* Left Side - Image with Overlay */}
        <div className="relative min-h-[400px] lg:min-h-[700px]">
          <img
            src="https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&ixlib=rb-4.0.3&q=85&w=1200"
            alt="Freight trucks and logistics operations"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-navy-900/80" />
          <div className="absolute inset-0 flex items-center justify-center p-8 lg:p-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center lg:text-left max-w-lg"
            >
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase leading-tight">
                Request a{' '}
                <span className="text-orange-500">Freight Quote</span>
              </h2>
              <p className="mt-4 text-lg text-slate-300 font-body">
                Get competitive freight rates with fast, reliable service.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="bg-navy-900 p-8 lg:p-12 xl:p-16">
          {isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-12 h-full flex flex-col items-center justify-center"
              data-testid="quote-success-message"
            >
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-white" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-white mb-4">
                Quote Request Received!
              </h3>
              <p className="font-body text-slate-400 max-w-md">
                Thank you for your request. One of our freight specialists will review your details and follow up shortly.
              </p>
            </motion.div>
          ) : (
            <motion.form
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="font-body font-medium text-white text-sm">
                    Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Your Name"
                    className="mt-2 bg-white border-0 text-navy-900 placeholder:text-slate-400"
                    data-testid="quote-input-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="font-body font-medium text-white text-sm">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="you@company.com"
                    className="mt-2 bg-white border-0 text-navy-900 placeholder:text-slate-400"
                    data-testid="quote-input-email"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="font-body font-medium text-white text-sm">
                  Phone *
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  placeholder="(555) 123-4567"
                  className="mt-2 bg-white border-0 text-navy-900 placeholder:text-slate-400"
                  data-testid="quote-input-phone"
                />
              </div>

              <div>
                <Label className="font-body font-medium text-white text-sm">Freight Type *</Label>
                <Select
                  value={formData.freight_type}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, freight_type: value }))
                  }
                  required
                >
                  <SelectTrigger 
                    className="mt-2 bg-white border-0 text-navy-900" 
                    data-testid="quote-select-freight-type"
                  >
                    <SelectValue placeholder="Select freight type" />
                  </SelectTrigger>
                  <SelectContent>
                    {freightTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="pickup_zip" className="font-body font-medium text-white text-sm">
                    Pickup ZIP Code *
                  </Label>
                  <Input
                    id="pickup_zip"
                    name="pickup_zip"
                    value={formData.pickup_zip}
                    onChange={handleInputChange}
                    required
                    placeholder="12345"
                    className="mt-2 bg-white border-0 text-navy-900 placeholder:text-slate-400"
                    data-testid="quote-input-pickup-zip"
                  />
                </div>
                <div>
                  <Label htmlFor="delivery_zip" className="font-body font-medium text-white text-sm">
                    Delivery ZIP Code *
                  </Label>
                  <Input
                    id="delivery_zip"
                    name="delivery_zip"
                    value={formData.delivery_zip}
                    onChange={handleInputChange}
                    required
                    placeholder="67890"
                    className="mt-2 bg-white border-0 text-navy-900 placeholder:text-slate-400"
                    data-testid="quote-input-delivery-zip"
                  />
                </div>
              </div>

              {/* Accessorials */}
              <div>
                <Label className="font-body font-medium text-white text-sm mb-3 block">
                  Accessorials
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {accessorialOptions.map((option) => (
                    <div key={option.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={option.id}
                        checked={selectedAccessorials.includes(option.id)}
                        onCheckedChange={(checked) =>
                          handleAccessorialChange(option.id, checked)
                        }
                        className="border-white data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                        data-testid={`quote-checkbox-${option.id}`}
                      />
                      <label
                        htmlFor={option.id}
                        className="font-body text-sm text-slate-300 cursor-pointer"
                      >
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="description" className="font-body font-medium text-white text-sm">
                  Freight Description / Special Instructions
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe your freight: dimensions, special handling requirements, timeline, etc."
                  className="mt-2 bg-white border-0 text-navy-900 placeholder:text-slate-400 min-h-[100px]"
                  data-testid="quote-input-description"
                />
              </div>

              {submitError && (
                <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
                  <p className="font-body text-sm text-red-400">{submitError}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-body font-bold text-base uppercase tracking-wide py-6 rounded-sm shadow-lg hover:shadow-orange-500/40 transition-all duration-300"
                data-testid="quote-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Get Freight Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </motion.form>
          )}
        </div>
      </div>
    </section>
  );
};
